// ============================================
// REPORTEMATUTINO.JS — Reporte ejecutivo diario de Beep
// ============================================
import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config/api';

const PP = "'Poppins', sans-serif";
const STORAGE_KEY = 'beep_reporte_matutino';

function leer(key, def) {
  try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : def; } catch { return def; }
}

export function recopilarDatosSistema() {
  const servicios = leer('beep_servicios', []);
  const proyectos = leer('beep_proyectos', []);
  const cotizaciones = leer('beep_cotizaciones_pipeline', []);
  const gastos = leer('beep_gastos', []);
  const aprobaciones = leer('beep_aprobaciones', []);
  const tareas = leer('beep_tareas', []);
  const inv = leer('beep_inventario', []);
  const hoyStr = new Date().toDateString();

  return {
    fecha: new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' }),
    servicios: {
      total: servicios.length,
      enCampo: servicios.filter(s => ['en_proceso','en_sitio','en_camino'].includes(s.estado)).length,
      completados: servicios.filter(s => s.estado === 'completado').length,
      nuevos: servicios.filter(s => s.estado === 'nuevo').length,
    },
    proyectos: {
      activos: proyectos.filter(p => ['en_ejecucion','anticipo'].includes(p.estado)).length,
      enCotizacion: proyectos.filter(p => p.estado === 'cotizacion').length,
    },
    cotizaciones: {
      activas: cotizaciones.filter(c => !['ganada','perdida','vencida'].includes(c.estadoCalculado || c.estado)).length,
      porVencer: cotizaciones.filter(c => c.estadoCalculado === 'por_vencer').length,
      sinSeguimiento: cotizaciones.filter(c => ['seguimiento_1','seguimiento_2'].includes(c.estadoCalculado)).length,
    },
    gastos: {
      pendientes: gastos.filter(g => g.estado === 'pendiente').length,
      montoTotal: gastos.filter(g => g.estado === 'pendiente').reduce((sum, g) => sum + (g.monto || 0), 0),
    },
    aprobaciones: {
      pendientes: aprobaciones.filter(a => a.estado === 'pendiente').length,
    },
    tareas: {
      urgentes: tareas.filter(t => t.prioridad === 'alta' && t.estado !== 'completada').length,
      hoy: tareas.filter(t => {
        if (!t.fechaVencimiento) return false;
        return new Date(t.fechaVencimiento).toDateString() === hoyStr && t.estado !== 'completada';
      }).length,
    },
    inventario: {
      stockBajo: inv.filter(i => i.cantidad <= i.cantidadMinima).length,
    },
  };
}

export async function generarReporteMatutino(nombre) {
  const datos = recopilarDatosSistema();
  // FIX-2X: zona horaria del navegador para que el backend formatee la hora
  // local del usuario (no la del servidor en UTC).
  let tz;
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { tz = null; }
  const res = await fetch(API_BASE + '/api/reporte-matutino', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ datos, nombre: nombre || 'Ricardo', tz }),
  });
  const data = await res.json();
  return { reporte: data.reporte, tipo: data.tipo || 'texto' };
}

const Skeleton = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    {[100, 80, 90].map((w, i) => (
      <div key={i} style={{
        height: 14, width: w + '%', borderRadius: 4,
        background: 'linear-gradient(90deg, #e0e8f0 25%, #c8d8e8 50%, #e0e8f0 75%)',
        backgroundSize: '200% 100%',
        animation: 'skeleton-shimmer 1.5s infinite',
      }} />
    ))}
  </div>
);

export default function ReporteMatutino({ nombre, rol, onNavegar }) {
  const [reporte, setReporte] = useState(null);
  const [tipo, setTipo] = useState('texto');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  const cargar = async (forzar = false) => {
    const hoy = new Date().toDateString();
    const guardado = leer(STORAGE_KEY, null);
    if (!forzar && guardado && guardado.fecha === hoy) {
      setReporte(guardado.texto);
      setTipo(guardado.tipo || 'texto');
      return;
    }
    setCargando(true);
    setError(null);
    try {
      const { reporte: r, tipo: t } = await generarReporteMatutino(nombre);
      setReporte(r);
      setTipo(t);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ fecha: hoy, texto: r, tipo: t })); } catch {}
    } catch (e) {
      setError('No pude generar el reporte. Intenta de nuevo en un momento.');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const horaActual = new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  const primerNombre = (nombre || 'Ricardo').split(' ')[0];

  return (
    <div style={{
      fontFamily: PP,
      background: '#E6F1FB',
      borderLeft: '4px solid #185FA5',
      borderRadius: 14,
      padding: 16,
      boxShadow: '0 1px 6px rgba(12,68,124,0.08)',
      maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden',
    }}>
      <style>{`@keyframes skeleton-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%',
          background: '#185FA5', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, flexShrink: 0,
          boxShadow: '0 2px 8px rgba(24,95,165,0.35)',
        }}>🎯</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#0C447C' }}>
            Buenos días, {primerNombre}
          </div>
          <div style={{ fontSize: 11, color: '#185FA5', opacity: 0.8 }}>
            Beep · {horaActual}
          </div>
        </div>
        <span style={{
          background: '#fff', color: '#185FA5',
          fontSize: 10, fontWeight: 800,
          padding: '4px 10px', borderRadius: 999,
          border: '1px solid #C8DEF2',
          flexShrink: 0,
        }}>
          Reporte de hoy
        </span>
      </div>

      {cargando && !reporte ? (
        <div style={{ background: '#fff', borderRadius: 12, padding: 14, marginBottom: 12 }}>
          <Skeleton />
        </div>
      ) : error ? (
        <div style={{ background: '#fff', borderRadius: 12, padding: 14, marginBottom: 12, color: '#9B2C2C', fontSize: 13 }}>
          {error}
        </div>
      ) : tipo === 'estructurado' && reporte && typeof reporte === 'object' ? (
        <div style={{ marginBottom: 12 }}>
          {reporte.saludo && (
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0C447C', marginBottom: 10 }}>
              {reporte.saludo}
            </div>
          )}
          {(reporte.secciones || []).map((sec, i) => {
            const urgente = !!sec.urgente;
            return (
              <div key={i} style={{
                background: urgente ? '#FFF0F0' : '#fff',
                border: `1px solid ${urgente ? '#F09595' : '#B5D4F4'}`,
                borderRadius: 12, padding: 14, marginBottom: 12,
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: urgente ? '#A32D2D' : '#0C447C', marginBottom: 8 }}>
                  {sec.titulo}
                </div>
                {(sec.items || []).map((it, j) => (
                  <div
                    key={j}
                    onClick={() => it.accion && onNavegar && onNavegar(it.accion)}
                    style={{
                      display: 'flex', alignItems: 'center',
                      padding: '10px 12px', borderRadius: 8,
                      background: 'white', cursor: it.accion ? 'pointer' : 'default',
                      border: `1px solid ${urgente ? '#F7C1C1' : '#D5E5F5'}`, marginBottom: 6,
                      transition: 'all 0.2s',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>{it.texto}</div>
                    </div>
                    {it.badge != null && it.badge !== '' && (
                      <div style={{
                        background: urgente ? '#E24B4A' : '#185FA5', color: 'white',
                        borderRadius: 99, padding: '3px 10px',
                        fontSize: 12, fontWeight: 700, marginRight: 8,
                      }}>{it.badge}</div>
                    )}
                    {it.accion && (
                      <div style={{ fontSize: 16, color: urgente ? '#E24B4A' : '#185FA5' }}>→</div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
          {reporte.cierre && (
            <div style={{ fontSize: 12, color: '#5e6770', fontStyle: 'italic', textAlign: 'center' }}>
              {reporte.cierre}
            </div>
          )}
        </div>
      ) : (
        <div style={{
          background: '#fff', borderRadius: 12, padding: 14, marginBottom: 12,
          fontSize: 14, lineHeight: 1.55, color: '#1a2a3a',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {typeof reporte === 'string' ? reporte : JSON.stringify(reporte)}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          onClick={() => cargar(true)}
          disabled={cargando}
          style={{
            background: 'transparent', color: '#5e6770',
            border: '1px solid #c8d8e8', borderRadius: 8,
            padding: '6px 12px', fontSize: 11, fontWeight: 600,
            cursor: cargando ? 'wait' : 'pointer', fontFamily: PP,
          }}
        >
          🔄 {cargando ? 'Actualizando…' : 'Actualizar reporte'}
        </button>
        <button
          onClick={() => onNavegar && onNavegar('mitrabajo.agentes-chat', 'beep')}
          style={{
            background: '#185FA5', color: '#fff',
            border: 'none', borderRadius: 8,
            padding: '6px 14px', fontSize: 11, fontWeight: 700,
            cursor: 'pointer', fontFamily: PP,
            marginLeft: 'auto',
          }}
        >
          💬 Hablar con Beep
        </button>
      </div>
    </div>
  );
}
